import { InfoTip } from "@/components/ui/info-tip";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Fragment, useEffect, useMemo, useState } from "react";
import { BellOff, ChevronLeft, ChevronRight, Download, Info } from "lucide-react";
import { ApiError } from "@/lib/api";
import {
  activeFilterCount,
  fetchReportSatisfaction,
  fetchSatisfactionRows,
  satisfactionCsvHref,
  lastDays,
  toSearchParams,
  RATING_REASON_LABELS,
  type ReportQuery,
  type ReportSatisfaction,
  type SatisfactionAgent,
  type SatisfactionBreakdown,
  type SatisfactionRow,
} from "@/lib/reports";
import {
  ClearFilters,
  MultiSelectFilter,
  RangePresets,
  type ReportFilterOption,
} from "@/components/reports/report-filters";
import { SatisfactionTrendChart } from "@/components/reports/report-charts";
import { useZone } from "@/components/session-provider";

export const Route = createFileRoute("/reports/satisfaction")({
  component: SatisfactionPage,
});

/**
 * Must stay in step with `ReportRequestBuilder.DefaultRangeDays`, as the activity report's does.
 * The server applies its own default to a request with no range, so a disagreement means a link
 * built by hand covers a different period from the screen it came from.
 */
const DEFAULT_DAYS = 30;
const PAGE_SIZE = 25;

/**
 * The answers panel, so pressing a name in the agent table can bring it into view.
 *
 * ⚠️ A control that loads a list somewhere below the fold reads as a control that did nothing,
 * and the second press is the one that files a second page of PHI read events.
 */
const ANSWERS_PANEL_ID = "satisfaction-answers";

/** Whose answers the drill-through is showing. `key` is a user id, or `"none"`. */
interface AgentSelection {
  readonly key: string;
  readonly label: string;
}

/**
 * The satisfaction report.
 *
 * ⚠️ **The same cohort as every other figure on this surface** — requests RAISED in the window
 * — so a reader moving between this screen and Request Activity is not silently changing the
 * question. Both take the identical query object and the server answers both from one pinned
 * base query.
 *
 * ⚠️ **It runs on the reader's real clearance.** This surface sits outside the D-65
 * classification elevation, so two administrators can quote different satisfaction figures for
 * the same week and both be right. The page says so above the first number rather than leaving
 * it to be discovered.
 *
 * ⚠️ **Measures, never a verdict (D-55).** There is no score, no rank and no medal on this
 * screen, and nothing here computes one — the agent table reports what requesters said beside
 * how many said it, star by star, and the reader sorts by one named measure with the basis
 * printed beside it. A composite would be a performance record about named colleagues in a
 * HIPAA-scoped system.
 *
 * ⚠️ **Nothing on this screen is withheld from a reader who can reach it (D-130).** What
 * requesters wrote, who worked each request, and how many of each star every colleague was
 * given are all here. The one thing deliberately staged is the requester's own sentence: it is
 * PHI-capable free text (D-63), so it lives on the drill-through, which is fetched only when
 * somebody asks for it and which files a PHI read against the reader's name for every row it
 * returns. That is a bound on WHEN it is disclosed and on what the ledger records — never on
 * who may see it.
 */
function SatisfactionPage() {
  const zone = useZone();

  // URL state, so a link shares the QUESTION rather than the answer. The recipient's own tier,
  // query filter and RLS decide what comes back (D-67).
  const [query, setQuery] = useState<ReportQuery>(() => readQueryFromUrl(zone));
  const [days, setDays] = useState<number | null>(DEFAULT_DAYS);

  const [report, setReport] = useState<ReportSatisfaction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * Filter options captured from the FIRST load and kept (D-58).
   *
   * ⚠️ Rebuilding them from each filtered response makes every selection remove the others —
   * the filter bar that traps you in whatever you picked first — and describes the slice on
   * screen rather than the reader's whole readable set.
   */
  const [baseline, setBaseline] = useState<ReportSatisfaction | null>(null);

  /**
   * Whose answers the list below is showing, set by pressing a name in the agent table.
   *
   * ⚠️ Held HERE rather than inside the answers panel, because the agent table is what sets it
   * and the two have to agree about who is selected — a table highlighting one colleague over a
   * list of somebody else's answers is a page making a claim about the wrong person.
   *
   * ⚠️ Deliberately NOT in the URL, unlike the filters. Every other value in the address bar
   * narrows an aggregate; this one opens a page of named requests and files a PHI read against
   * the reader for each of them, so a link that arrives pre-loaded would audit the recipient for
   * rows they never asked to see. The link shares the question; the reader presses the name.
   */
  const [agent, setAgent] = useState<AgentSelection | null>(null);

  const serialised = toSearchParams(query).toString();

  useEffect(() => {
    const url = serialised ? `${window.location.pathname}?${serialised}` : window.location.pathname;
    window.history.replaceState(null, "", url);
  }, [serialised]);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    // ⚠️ Cleared rather than left on screen. Stale figures beside a fresh range label look
    // exactly like current ones, and nothing about a percentage says which week it is about.
    setReport(null);
    setError(null);
    setLoading(true);

    void (async () => {
      try {
        const result = await fetchReportSatisfaction(query, controller.signal);
        if (cancelled) return;

        setBaseline((current) => current ?? result);
        setReport(result);
        setLoading(false);
      } catch (e) {
        if (cancelled || controller.signal.aborted) return;

        setError(
          e instanceof ApiError ? e.message : "The satisfaction report could not be loaded.",
        );
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [query]);

  const options = useMemo(() => buildOptions(baseline), [baseline]);

  const setRange = (nextDays: number) => {
    setDays(nextDays);
    setQuery((current) => ({ ...current, ...lastDays(nextDays, zone) }));
  };

  const narrow = (patch: Partial<ReportQuery>) => setQuery((current) => ({ ...current, ...patch }));
  const clear = () => setQuery((current) => ({ fromUtc: current.fromUtc, toUtc: current.toUtc }));

  const filters = activeFilterCount(query);
  const head = report?.headline;

  return (
    <div className="mx-auto w-full max-w-7xl space-y-6 p-4 sm:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to="/reports"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
            All reports
          </Link>

          <h1 className="mt-1 text-xl font-semibold">Satisfaction</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            What the people who raised requests said, how many of them answered, and what the
            unhappy ones said had gone wrong.
          </p>
        </div>

        {/*
          ⚠️ AGGREGATES ONLY, and the label says so. The individual answers are deliberately not
          in the file: a spreadsheet of who said what about which colleague, leaving the system
          permanently and forwardable for ever, is a bulk export — which keeps its own
          permission, a written justification, a row cap and a watermark (D-57, D-81).
        */}
        <a
          href={satisfactionCsvHref(query)}
          className="flex h-9 items-center gap-1.5 rounded-lg border border-border px-3 text-xs font-medium transition-colors hover:bg-muted"
        >
          <Download className="h-3.5 w-3.5" aria-hidden />
          Export figures (CSV)
        </a>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <RangePresets activeDays={days} onSelect={setRange} disabled={loading} />

        <MultiSelectFilter
          label="Team"
          options={options.teams}
          selected={query.teamIds ?? []}
          onChange={(teamIds) => narrow({ teamIds })}
        />
        <MultiSelectFilter
          label="Site"
          options={options.sites}
          selected={query.siteIds ?? []}
          onChange={(siteIds) => narrow({ siteIds })}
        />
        <MultiSelectFilter
          label="Category"
          options={options.categories}
          selected={query.categoryIds ?? []}
          onChange={(categoryIds) => narrow({ categoryIds })}
        />

        {filters > 0 && <ClearFilters onClear={clear} />}
      </div>

      {/*
        What these figures cover, above the first number, always. Describes the READER'S ACCESS
        and never the data — there is no count of what is missing here or on the server, because
        "there are 47 answers you cannot see" is the enumeration oracle this surface avoids.

        ⚠️ An applied filter wins the headline (D-58): somebody who narrowed to two teams and
        forgot is the reader most likely to quote a partial figure as a whole one.
      */}
      {report && (
        <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <p>
            {filters > 0
              ? `Narrowed by ${filters} filter${filters === 1 ? "" : "s"}. `
              : report.scope.isOwnWorkOnly
                ? "Your own work — requests assigned to you or raised by you. "
                : "Every request you can read. "}
            Ratings on requests <strong>raised</strong> in this period, wherever the answer arrived.
            Days shown in {report.timeZoneId.replace(/_/g, " ")}. Two people with access to
            different queues will see different figures here, and both are right.
          </p>
        </div>
      )}

      {/*
        ⚠️ Said out loud when the surveys are off, because otherwise a response rate falling
        toward nothing reads as a desk people have given up on. Reading a rate is not asking
        anybody anything, so the figures are still computed and a rating already given is never
        hidden by this flag (D-63).
      */}
      {report && !report.surveysEnabled && (
        <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/5 p-3 text-xs">
          <BellOff className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" aria-hidden />
          <p>
            <strong>Nobody is being asked to rate anything at the moment.</strong> Survey
            notifications are switched off, so the response rate below is falling for that reason
            rather than because people have stopped answering. Ratings already given are unaffected.
          </p>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {loading && !report && (
        <p className="py-12 text-center text-sm text-muted-foreground">Loading…</p>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* Headline.                                                         */}
      {/* ---------------------------------------------------------------- */}
      {report && head && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Tile
            label="Satisfied"
            text={percent(head.csat)}
            delta={points(head.csat, head.csatPrevious)}
            note={
              head.responses === 0
                ? "Nobody has answered yet"
                : `${head.satisfied.toLocaleString()} of ${head.responses.toLocaleString()} answers were ${report.followUpBelowStars} stars or more`
            }
          />

          <Tile
            label="Answered"
            value={head.responses}
            note={`${head.responsesPrevious.toLocaleString()} in the previous period`}
          />

          {/*
            ⚠️ The denominator is named on the tile rather than left to the tooltip (D-53). This
            figure is REACH — requests old enough to have been asked about — so a requester who
            silenced survey mail counts in it and answers nothing. Calling it "response rate"
            without saying what it is over is how it gets quoted as a reply rate.
          */}
          <Tile
            label="Answered, of what we could ask about"
            text={percent(head.responseRate)}
            note={`${head.responses.toLocaleString()} of ${head.eligible.toLocaleString()} requests resolved more than ${report.surveyDelayHours}h ago`}
          />

          {/*
            ⚠️ The closed loop, and the reason the rest of this page is more than a scoreboard
            (BL-034). Its denominator is DISSATISFIED answers — the same one the reasons use —
            so "eleven people were unhappy, four have been looked at again" is one sentence
            about one set rather than two figures a reader has to reconcile.

            ⚠️ It reports how many REQUESTS were looked at again, never by whom. A follow-up
            count per colleague is a responsiveness league table about named staff (D-24, D-55).
          */}
          <Tile
            label="Low ratings looked at again"
            text={percent(head.reviewedRate)}
            note={
              head.dissatisfied === 0
                ? "Nobody rated low in this period"
                : `${head.reviewed.toLocaleString()} of ${head.dissatisfied.toLocaleString()} — nobody is told, it is for the desk`
            }
          />

          <Tile
            label="Average rating"
            text={head.averageStars === null ? "—" : head.averageStars.toFixed(2)}
            note={
              head.responses === 0
                ? "No ratings in this period"
                : `Over ${head.responses.toLocaleString()} answer${head.responses === 1 ? "" : "s"}`
            }
          />
        </div>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* Trend and distribution.                                           */}
      {/* ---------------------------------------------------------------- */}
      {report && head && (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Panel
              help="sat-trend"
              title="Satisfaction over time"
              subtitle="By the day each request was raised, not the day it was answered"
            >
              <SatisfactionTrendChart points={report.trend} timeZoneId={report.timeZoneId} />
            </Panel>
          </div>

          <Panel
            help="sat-distribution"
            title="How they rated"
            subtitle={`${head.responses.toLocaleString()} answers`}
          >
            <Distribution
              distribution={head.distribution}
              followUpBelowStars={report.followUpBelowStars}
              total={head.responses}
            />
          </Panel>
        </div>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* What went wrong. The actionable half.                             */}
      {/* ---------------------------------------------------------------- */}
      {report && head && (
        <Panel
          title="What the unhappy ones said"
          subtitle={
            head.dissatisfied === 0
              ? "Nobody rated below the follow-up threshold in this period"
              : `Chosen from a fixed list by ${head.dissatisfied.toLocaleString()} requester${head.dissatisfied === 1 ? "" : "s"} who rated below ${report.followUpBelowStars} stars. One answer can name several, so these add up to more than 100%.`
          }
        >
          {report.reasons.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {head.dissatisfied === 0
                ? "Nothing to show — no low ratings in this period."
                : "Nobody who rated low chose a reason. Answering is never required (D-63)."}
            </p>
          ) : (
            <ul className="space-y-2">
              {report.reasons.map((reason) => (
                <li key={reason.reason} className="flex items-center gap-3">
                  <span className="w-56 shrink-0 truncate text-xs">
                    {RATING_REASON_LABELS[reason.reason] ?? reason.reason}
                  </span>

                  <span className="h-2 min-w-1 flex-1 rounded-full bg-muted">
                    <span
                      className="block h-2 rounded-full bg-[var(--chart-2)]"
                      style={{ width: `${reason.shareOfDissatisfied ?? 0}%` }}
                    />
                  </span>

                  <span className="w-28 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                    {reason.count.toLocaleString()} · {percent(reason.shareOfDissatisfied)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* Breakdowns. Every one reconciles to the headline (D-54).          */}
      {/* ---------------------------------------------------------------- */}
      {report && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Panel
            help="sat-team"
            title="By team"
            subtitle="The team that held the request when it was rated, so routing it afterwards does not move history"
          >
            <BreakdownTable
              breakdown={report.byTeam}
              onSelect={(key) => narrow({ teamIds: [key] })}
            />
          </Panel>

          <Panel title="By site" help="sat-site">
            <BreakdownTable
              breakdown={report.bySite}
              onSelect={(key) => narrow({ siteIds: [key] })}
            />
          </Panel>

          <Panel title="By category" help="sat-category">
            <BreakdownTable
              breakdown={report.byCategory}
              onSelect={(key) => narrow({ categoryIds: [key] })}
            />
          </Panel>

          <Panel title="By priority" help="sat-priority">
            <BreakdownTable breakdown={report.byPriority} />
          </Panel>
        </div>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* Per agent.                                                        */}
      {/* ---------------------------------------------------------------- */}
      {report?.byAgent && (
        <AgentTable
          rows={report.byAgent}
          floor={report.minimumResponsesToRate}
          total={report.headline.responses}
          selected={agent?.key ?? null}
          onSelect={(selection) => {
            setAgent(selection);

            // Brought into view rather than left below the fold: a press that appears to do
            // nothing is a press somebody repeats, and the repeat is a second page of read
            // events about the same requests.
            document
              .getElementById(ANSWERS_PANEL_ID)
              ?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
        />
      )}

      {/* ---------------------------------------------------------------- */}
      {/* The answers themselves.                                           */}
      {/* ---------------------------------------------------------------- */}
      {report && <RatingRows query={query} agent={agent} onClearAgent={() => setAgent(null)} />}

      {report && (
        <Definitions
          followUpBelowStars={report.followUpBelowStars}
          surveyDelayHours={report.surveyDelayHours}
          floor={report.minimumResponsesToRate}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * The star distribution.
 *
 * ⚠️ Two states, not five hues. Below the follow-up threshold is the one distinction the desk
 * acts on (D-63), and it is a STATUS rather than an identity — so it is drawn in the warning
 * colour and named in words, never in colour alone.
 */
function Distribution({
  distribution,
  followUpBelowStars,
  total,
}: {
  distribution: readonly number[];
  followUpBelowStars: number;
  total: number;
}) {
  if (total === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        Nothing raised in this period has been rated yet.
      </p>
    );
  }

  // Highest first: somebody reading this is looking for the shape of the bottom end, and a
  // 5-to-1 order puts it where the eye finishes rather than where it starts.
  const rows = distribution.map((count, index) => ({ stars: index + 1, count })).reverse();

  return (
    <ul className="space-y-2">
      {rows.map((row) => {
        const low = row.stars < followUpBelowStars;

        return (
          <li key={row.stars} className="flex items-center gap-3">
            <span className="w-10 shrink-0 text-xs tabular-nums text-muted-foreground">
              {row.stars}★
            </span>

            <span className="h-2 min-w-1 flex-1 rounded-full bg-muted">
              <span
                className="block h-2 rounded-full"
                style={{
                  width: `${total === 0 ? 0 : (100 * row.count) / total}%`,
                  backgroundColor: low ? "var(--chart-2)" : "var(--chart-1)",
                }}
              />
            </span>

            <span className="w-10 shrink-0 text-right text-xs tabular-nums">{row.count}</span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * One breakdown as a table rather than a chart.
 *
 * ⚠️ A table because each row carries THREE numbers that have to be read together — the count,
 * the rate and the average — and a bar chart of the rate alone puts a group of two answers
 * beside a group of two hundred at the same length. The count is what stops that being
 * misleading, so it has to be on screen rather than in a tooltip (D-53).
 *
 * ⚠️ The total is printed, so the reader can see the column adds up rather than adding it up
 * (D-54). No small-cell suppression: an n=1 row describes a request this reader can already
 * open, and hiding it would break the reconciliation while protecting nothing.
 */
function BreakdownTable({
  breakdown,
  onSelect,
}: {
  breakdown: SatisfactionBreakdown;
  onSelect?: (key: string) => void;
}) {
  if (breakdown.rows.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">Nothing in this period.</p>
    );
  }

  return (
    <>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border text-muted-foreground">
            <th className="py-1.5 text-left font-medium">Group</th>
            <th className="py-1.5 text-right font-medium">Answers</th>
            <th className="py-1.5 text-right font-medium">Satisfied</th>
            <th className="py-1.5 text-right font-medium">Average</th>
          </tr>
        </thead>
        <tbody>
          {breakdown.rows.map((row) => (
            <tr key={row.label} className="border-b border-border/60 last:border-0">
              <td className="py-1.5 pr-2">
                {/*
                  ⚠️ Drillable only where the row IS one group. "Other" stands for several and
                  has nothing to send as a filter; sending its label would produce an empty
                  table under a heading saying otherwise.
                */}
                {row.key && onSelect ? (
                  <button
                    type="button"
                    onClick={() => onSelect(row.key!)}
                    className="inline-flex items-center gap-0.5 truncate text-left hover:underline"
                  >
                    {row.label}
                    <ChevronRight className="h-3 w-3 shrink-0 opacity-50" aria-hidden />
                  </button>
                ) : (
                  <span className="truncate">{row.label}</span>
                )}
              </td>
              <td className="py-1.5 text-right tabular-nums">{row.responses.toLocaleString()}</td>
              <td className="py-1.5 text-right tabular-nums">{percent(row.csat)}</td>
              <td className="py-1.5 text-right tabular-nums text-muted-foreground">
                {row.averageStars === null ? "—" : row.averageStars.toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="mt-2 text-[11px] text-muted-foreground">
        Adds up to {breakdown.total.toLocaleString()} answer
        {breakdown.total === 1 ? "" : "s"} — the same figure as the headline.
      </p>
    </>
  );
}

type AgentSort = "responses" | "csat" | "average" | "dissatisfied";

const AGENT_SORT_LABELS: Readonly<Record<AgentSort, string>> = {
  responses: "answers",
  csat: "satisfied",
  average: "average rating",
  dissatisfied: "unhappy answers",
};

/**
 * What each agent's requesters said, star by star.
 *
 * ⚠️ **MEASURES, NEVER A VERDICT (D-55).** No composite, no rank formula, no medals. The reader
 * chooses ONE named measure to order by and the column header says which — a ranking somebody
 * can argue with is the only kind worth publishing about a colleague. The default is the answer
 * count, deliberately, so the page does not open on a league table.
 *
 * ⚠️ **The star counts are what make this honest rather than merely more detailed (D-130).**
 * An average of 3.4 is the same number for "everybody said three" and for "half said five and
 * half said one", and those are opposite facts about a desk — the first is a service problem,
 * the second a consistency one. A single number cannot be argued with; five counts and a
 * drill-through can.
 *
 * ⚠️ **A rate below the floor is MARKED, no longer withheld (D-130 amending D-64).** With the
 * star counts on the same row anybody can do the division, so a "—" in the satisfied column
 * concealed nothing and only looked like concealment. The row says the sample is small, the
 * count stays exact, and a small sample still sorts to the bottom of a rate column rather than
 * to the top — a 100% over two answers above somebody's 94% over ninety is a ranking by luck.
 *
 * ⚠️ The frame is what requesters said about requests RAISED in the period, attributed to
 * whoever held the request when it was rated. The card says so — an unlabelled agent table
 * invites the reading that it covers everything they have ever done.
 */
function AgentTable({
  rows,
  floor,
  total,
  selected,
  onSelect,
}: {
  rows: readonly SatisfactionAgent[];
  floor: number;
  total: number;
  selected: string | null;
  onSelect: (selection: AgentSelection) => void;
}) {
  const [sort, setSort] = useState<AgentSort>("responses");

  const sorted = useMemo(() => {
    const copy = [...rows];

    // ⚠️ A rate over a small sample sorts LAST in a rate column, and the reason is not
    // squeamishness: two five-star answers make a perfect score, and a table opening on that
    // above a colleague with ninety answers at 94% is ordered by sample size wearing the
    // costume of a measure. The figure is still printed; it just does not lead.
    const byRate = (value: number | null, small: boolean) =>
      value === null || small ? Number.NEGATIVE_INFINITY : value;

    switch (sort) {
      case "csat":
        return copy.sort((a, b) => byRate(b.csat, b.smallSample) - byRate(a.csat, a.smallSample));
      case "average":
        return copy.sort(
          (a, b) => byRate(b.averageStars, b.smallSample) - byRate(a.averageStars, a.smallSample),
        );
      case "dissatisfied":
        return copy.sort((a, b) => b.dissatisfied - a.dissatisfied);
      default:
        return copy.sort((a, b) => b.responses - a.responses);
    }
  }, [rows, sort]);

  return (
    <Panel
      title="By agent"
      subtitle={`What requesters said about requests raised in this period, attributed to whoever held each one when it was rated. Every answer is counted at the star it was given; rates over fewer than ${floor} answers are marked as a small sample, never hidden. Press a name to read that person's own answers, in the requester's words.`}
      action={
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          Order by
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as AgentSort)}
            className="h-8 rounded-lg border border-input bg-card px-2 text-xs"
          >
            <option value="responses">Answers</option>
            <option value="csat">Satisfied</option>
            <option value="average">Average rating</option>
            <option value="dissatisfied">Unhappy answers</option>
          </select>
        </label>
      }
    >
      {sorted.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          Nothing raised in this period has been rated yet.
        </p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-xs">
              <thead>
                <tr className="border-b border-border text-muted-foreground">
                  <th className="py-1.5 text-left font-medium">Agent</th>
                  <th className="py-1.5 text-right font-medium">Answers</th>

                  {/*
                    ⚠️ Five separate columns rather than a bar. A bar is read to the nearest
                    guess, and the whole point of publishing the distribution is that somebody
                    can check a claim about a colleague against the drill-through below.
                    Descending, so the column somebody scans for is the one on the left of the
                    group and the unhappy end is closest to the "Unhappy" total beside it.
                  */}
                  {[5, 4, 3, 2, 1].map((star) => (
                    <th
                      key={star}
                      className="py-1.5 text-right font-medium tabular-nums"
                      title={`How many people gave ${star} star${star === 1 ? "" : "s"}`}
                    >
                      {star}★
                    </th>
                  ))}

                  <th className="py-1.5 text-right font-medium">Unhappy</th>
                  <th className="py-1.5 text-right font-medium">Satisfied</th>
                  <th className="py-1.5 text-right font-medium">Average</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((row) => {
                  // ⚠️ "none" for the unassigned row, the token the server reads (D-111's
                  // spelling). Every row is pressable, including that one: a row that cannot open
                  // its own answers looks broken rather than deliberate, and requests resolved
                  // with nobody holding them are a real and interesting group on this list.
                  const key = row.key ?? "none";

                  return (
                    <tr
                      key={row.key ?? row.label}
                      className={`border-b border-border/60 last:border-0 ${
                        selected === key ? "bg-muted/50" : ""
                      }`}
                    >
                      <td className="max-w-[14rem] py-1.5 pr-2">
                        {/*
                        ⚠️ THE WAY INTO THE EVIDENCE, and the reason the numbers on this row are
                        publishable at all. A satisfaction figure about a named colleague that
                        nobody can check is the objection D-55 raises against a scorecard; the
                        answer to it is the answers themselves, one press away.

                        ⚠️ It narrows the list below and nothing else. It is not a filter on the
                        figures above, which stay the whole desk's — a press that quietly rewrote
                        the headline would make a coaching conversation start from a number the
                        person cannot reconcile with the report they were sent.
                      */}
                        <button
                          type="button"
                          onClick={() => onSelect({ key, label: row.label })}
                          className="block max-w-full truncate text-left hover:underline"
                          title={`Read the answers behind ${row.label}`}
                          aria-pressed={selected === key}
                        >
                          {row.label}
                        </button>
                        {row.smallSample && (
                          <span
                            className="ml-1.5 text-[10px] text-muted-foreground"
                            title={`Fewer than ${floor} answers — the rates beside this name are exact but rest on a small sample`}
                          >
                            small sample
                          </span>
                        )}
                      </td>

                      <td className="py-1.5 text-right tabular-nums">
                        {row.responses.toLocaleString()}
                      </td>

                      {[5, 4, 3, 2, 1].map((star) => {
                        const count = row.distribution[star - 1] ?? 0;

                        return (
                          <td
                            key={star}
                            className={`py-1.5 pl-2 text-right tabular-nums ${
                              count === 0
                                ? "text-muted-foreground/40"
                                : star < 4
                                  ? "text-amber-600"
                                  : "text-muted-foreground"
                            }`}
                          >
                            {count}
                          </td>
                        );
                      })}

                      <td className="py-1.5 pl-2 text-right tabular-nums">
                        {row.dissatisfied === 0 ? (
                          <span className="text-muted-foreground/40">0</span>
                        ) : (
                          <span className="text-amber-600">{row.dissatisfied}</span>
                        )}
                      </td>

                      {/*
                      ⚠️ Printed at every sample size (D-130). The count it is taken over is two
                      columns to the left and the stars behind it are between them, so there is
                      nothing here a reader has to take on trust.
                    */}
                      <td className="py-1.5 pl-2 text-right tabular-nums">
                        {row.csat === null ? "—" : percent(row.csat)}
                      </td>
                      <td className="py-1.5 pl-2 text-right tabular-nums text-muted-foreground">
                        {row.averageStars === null ? "—" : row.averageStars.toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="mt-2 text-[11px] text-muted-foreground">
            Adds up to {total.toLocaleString()} answer{total === 1 ? "" : "s"} — the same figure as
            the headline. Ordered by {AGENT_SORT_LABELS[sort]}; a rate over fewer than {floor}{" "}
            answers sorts last so that a perfect score over two answers does not lead the table.
            These are measures, not a score — nothing here is ranked, weighted or combined, and
            every figure can be checked against the answers below: press a name to read what that
            person's requesters actually wrote.
          </p>
        </>
      )}
    </Panel>
  );
}

/**
 * The individual answers behind the figures.
 *
 * ⚠️ **THE DRILL-THROUGH IS WHAT MAKES THE AGENT TABLE ARGUABLE.** A satisfaction number about
 * a named colleague that nobody can check is the objection D-55 raises against a scorecard; the
 * answer to it is the evidence, not fewer numbers.
 *
 * ⚠️ **Loaded on demand, not with the page.** Every row here files a PHI read event against the
 * reader's name (§164.312(b)), so opening the report must not audit a page of requests nobody
 * asked to see. The button is the moment somebody asked.
 *
 * ⚠️ **This is the one place in the product that prints what requesters wrote (D-130).** A
 * rating's free text is PHI-capable (D-63), and the bounds on it are all here rather than in a
 * missing column: the page is fetched only when somebody presses the button, every row files a
 * PHI read against the reader's name, and the caller's real clearance applies in both layers.
 * The CSV beside it stays aggregates-only, because a file leaves permanently and cannot be
 * recalled.
 *
 * ⚠️ **The sentence gets a row of its own rather than a truncated cell.** A complaint clipped
 * at forty characters is a quotation the reader has to open the request to finish, which is the
 * trip this column exists to save — and a half-quoted complaint is the kind that gets repeated
 * wrongly.
 */
function RatingRows({
  query,
  agent,
  onClearAgent,
}: {
  query: ReportQuery;
  agent: AgentSelection | null;
  onClearAgent: () => void;
}) {
  const [rows, setRows] = useState<readonly SatisfactionRow[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ⚠️ The actionable view (BL-034): low ratings nobody has been back to. This is what turns
  // the tile above into work — a figure that cannot open the rows it counted is D-70's defect
  // arriving on a second screen.
  const [outstandingOnly, setOutstandingOnly] = useState(false);

  // ⚠️ Reset when the question changes, rather than appending to a list built under a different
  // filter. Rows from two filters in one table is a list that matches no heading on the page.
  const serialised = toSearchParams(query).toString();
  const agentKey = agent?.key ?? null;

  const load = async (
    from: string | null,
    needsReview = outstandingOnly,
    forAgent: string | null = agentKey,
  ) => {
    setLoading(true);
    setError(null);

    try {
      const page = await fetchSatisfactionRows(query, from, PAGE_SIZE, needsReview, forAgent);

      setRows((current) => (from === null ? page.items : [...(current ?? []), ...page.items]));
      setCursor(page.nextCursor);
      setHasMore(page.hasMore);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "The answers could not be loaded.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Cleared when the question changes, and loaded straight away when somebody names an agent.
   *
   * ⚠️ Auto-loading is not a widening of when this audits: pressing a colleague's name in the
   * table above IS the moment somebody asked, exactly as the button is. What it must not do is
   * load on arrival, which is why the effect turns the list off again the moment the selection
   * goes and never loads with no press behind it.
   */
  useEffect(() => {
    setRows(null);
    setCursor(null);
    setHasMore(false);
    setError(null);

    if (agentKey !== null) void load(null, outstandingOnly, agentKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialised, outstandingOnly, agentKey]);

  return (
    <Panel
      id={ANSWERS_PANEL_ID}
      title={agent ? `Every answer — ${agent.label}` : "Every answer"}
      subtitle={
        agent
          ? `What requesters wrote about the requests ${agent.label} was holding when they were rated — the same attribution as the row you pressed, so the two cannot disagree. Opening this records a read against each request listed.`
          : rows !== null && outstandingOnly
            ? "Low ratings nobody has been back to, in the requester's own words. Opening this records a read against each request listed."
            : "The individual ratings behind the figures above, in the requester's own words. Opening this records a read against each request listed."
      }
      action={
        agent ? (
          <button
            type="button"
            onClick={onClearAgent}
            className="h-8 rounded-lg border border-input px-3 text-xs transition hover:bg-accent"
          >
            Show everyone's answers
          </button>
        ) : rows === null ? (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setOutstandingOnly(true);
                void load(null, true);
              }}
              disabled={loading}
              className="h-8 rounded-lg border border-input px-3 text-xs transition hover:bg-accent disabled:opacity-50"
            >
              Only the ones nobody has been back to
            </button>
            <button
              type="button"
              onClick={() => {
                setOutstandingOnly(false);
                void load(null, false);
              }}
              disabled={loading}
              className="h-8 rounded-lg border border-input px-3 text-xs transition hover:bg-accent disabled:opacity-50"
            >
              {loading ? "Loading…" : "Show every answer"}
            </button>
          </div>
        ) : undefined
      }
    >
      {error && <p className="py-3 text-sm text-destructive">{error}</p>}

      {rows === null && !error && (
        <p className="py-6 text-center text-sm text-muted-foreground">
          Not loaded. Each answer names a request and shows what the requester wrote about it, so
          listing them records a read against every request in the list.
        </p>
      )}

      {rows !== null && rows.length === 0 && (
        <p className="py-6 text-center text-sm text-muted-foreground">
          {agent
            ? outstandingOnly
              ? `Every low rating on ${agent.label}'s requests in this period has been looked at again.`
              : `Nothing ${agent.label} was holding was rated in this period.`
            : outstandingOnly
              ? "Every low rating in this period has been looked at again."
              : "Nothing raised in this period has been rated yet."}
        </p>
      )}

      {rows !== null && rows.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-xs">
              <thead>
                <tr className="border-b border-border text-muted-foreground">
                  <th className="py-1.5 text-left font-medium">Request</th>
                  <th className="py-1.5 text-left font-medium">Requester</th>
                  <th className="py-1.5 text-left font-medium">Agent</th>
                  <th className="py-1.5 text-left font-medium">Rating</th>
                  <th className="py-1.5 text-left font-medium">What went wrong</th>
                  <th className="py-1.5 text-left font-medium">Looked at again</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <Fragment key={row.number}>
                    <tr className={row.comment ? "" : "border-b border-border/60 last:border-0"}>
                      <td className="max-w-[22rem] py-2 pr-3 align-top">
                        {/*
                        ⚠️ Linked on the OPAQUE number, never the readable one (D-4). The readable
                        one is what somebody recognises and says aloud; it addresses nothing.
                      */}
                        <Link
                          to="/tickets/$number"
                          params={{ number: row.number }}
                          className="block truncate hover:underline"
                          title={row.subject}
                        >
                          #{row.displayNumber} · {row.subject}
                        </Link>
                      </td>
                      <td className="py-2 pr-3 align-top">{row.requesterName}</td>
                      <td className="py-2 pr-3 align-top text-muted-foreground">{row.agentName}</td>
                      <td className="py-2 pr-3 align-top whitespace-nowrap">
                        <span
                          className={row.stars < 4 ? "text-amber-600" : "text-muted-foreground"}
                          aria-label={`Rated ${row.stars} out of 5`}
                        >
                          <span aria-hidden="true">
                            {"★".repeat(row.stars)}
                            {"☆".repeat(5 - row.stars)}
                          </span>
                        </span>
                      </td>
                      <td className="py-2 align-top">
                        {row.reasons.length === 0 ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <span className="flex flex-wrap gap-1">
                            {row.reasons.map((reason) => (
                              <span
                                key={reason}
                                className="rounded-full border border-border px-1.5 py-0.5 text-[11px]"
                              >
                                {RATING_REASON_LABELS[reason] ?? reason}
                              </span>
                            ))}
                          </span>
                        )}
                      </td>
                      <td className="py-2 align-top whitespace-nowrap">
                        {/*
                        ⚠️ WHETHER, never BY WHOM. The reviewer is stored so the ledger can
                        answer "who has been marking complaints as handled" — a fair question of
                        an append-only record — and a name in this column would make the table a
                        record of colleagues' responsiveness (D-24, D-55).

                        ⚠️ "Not yet" only where one is actually needed. A five-star answer has
                        nothing to look at again, and marking it outstanding would put work on a
                        list that does not exist.
                      */}
                        {row.reviewed ? (
                          <span className="text-success">Yes</span>
                        ) : row.needsReview ? (
                          <span className="text-amber-600">Not yet</span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>

                    {/*
                    ⚠️ WHAT THE REQUESTER ACTUALLY WROTE (D-130), on a row of its own so it can
                    wrap at full width. Verbatim and untruncated: a complaint clipped mid-sentence
                    is one somebody has to open the request to finish, and a half-read complaint
                    is the kind that gets repeated wrongly.

                    ⚠️ Rendered as TEXT, never as markup. React escapes it, and
                    `dangerouslySetInnerHTML` is banned outright — this is free text somebody
                    outside the desk typed, which is the exact input that rule exists for.

                    ⚠️ A row with no comment renders NOTHING here rather than an empty quotation.
                    Most people rate without typing anything, and a blank pair of quote marks
                    reads as text that failed to load.
                  */}
                    {row.comment && (
                      <tr className="border-b border-border/60 last:border-0">
                        <td colSpan={6} className="pb-2.5 pl-3 pr-3">
                          <blockquote className="border-l-2 border-border pl-3 text-[12px] leading-relaxed text-foreground/90">
                            {row.comment}
                          </blockquote>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {hasMore && (
            <div className="mt-3 flex justify-center">
              <button
                type="button"
                onClick={() => void load(cursor)}
                disabled={loading}
                className="h-9 rounded-lg border border-input px-4 text-xs transition hover:bg-accent disabled:opacity-50"
              >
                {loading ? "Loading…" : "Load more"}
              </button>
            </div>
          )}

          {/*
            ⚠️ THIS PARAGRAPH SAID THE OPPOSITE UNTIL NOW, and the correction matters more than
            the wording. It read "the comment somebody left is not shown here", which was true
            before D-130 and false the moment the blockquote above it shipped — so the one screen
            that publishes what requesters wrote was also the screen telling its readers it did
            not. A lead looking for the sentence behind a low rating read that line and stopped.

            ⚠️ Copy that describes a control is part of the control (D-114): a page that
            misdescribes what it is showing is not a smaller problem than a page showing the wrong
            thing, because both end with somebody believing something untrue about the data.
          */}
          <p className="mt-2 text-[11px] text-muted-foreground">
            Each answer shows what the requester wrote, word for word, where they wrote anything —
            most people rate without typing. Marking that you have been back to a low rating is on
            the request itself, and nobody is told when you do.
          </p>
        </>
      )}
    </Panel>
  );
}

/**
 * What each figure counts, in the reader's own words.
 *
 * ⚠️ Not decoration. D-53 has five quality rates that deliberately do not share a denominator,
 * and the cost of guessing wrong about which is which is a figure quoted in the wrong context —
 * on this page, about named colleagues.
 */
function Definitions({
  followUpBelowStars,
  surveyDelayHours,
  floor,
}: {
  followUpBelowStars: number;
  surveyDelayHours: number;
  floor: number;
}) {
  return (
    <section className="rounded-xl border border-border bg-muted/30 p-4 text-xs text-muted-foreground">
      <h2 className="text-sm font-semibold text-foreground">What these figures count</h2>

      <dl className="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          <dt className="font-medium text-foreground">Satisfied</dt>
          <dd>
            The share of answers at {followUpBelowStars} stars or more. Below that the requester is
            offered a fixed list of what could have gone better, and a box to write in. Both are on
            every answer in the list below — the fixed reasons as labels, and whatever they wrote
            word for word.
          </dd>
        </div>

        <div>
          <dt className="font-medium text-foreground">Answered, of what we could ask about</dt>
          <dd>
            Answers divided by the requests resolved more than {surveyDelayHours} hours ago — which
            is when the survey goes out. It measures <strong>reach</strong>, not reply rate:
            somebody who switched survey emails off counts in the denominator and answers nothing. A
            request that was withdrawn, or closed without being resolved, is never asked about and
            is in neither half.
          </dd>
        </div>

        <div>
          <dt className="font-medium text-foreground">The period</dt>
          <dd>
            Requests <strong>raised</strong> in the range, wherever the answer arrived — the same
            cohort as every other figure on this surface. The most recent days are therefore thin
            and rise as ratings land.
          </dd>
        </div>

        <div>
          <dt className="font-medium text-foreground">Low ratings looked at again</dt>
          <dd>
            How many of the answers below {followUpBelowStars} stars somebody on the desk has marked
            as having been back to. It records that a colleague went and looked —{" "}
            <strong>not</strong> that the requester was contacted, which the product cannot observe.
            Nobody is told it happened; it is here so an unhappy request does not go quiet.
          </dd>
        </div>

        <div>
          <dt className="font-medium text-foreground">Small samples</dt>
          <dd>
            A rate over fewer than {floor} answers is <strong>marked</strong>, not withheld: the
            star counts are on the same row, so anybody can do the division and a blank concealed
            nothing. It still sorts to the bottom of a rate column, because a perfect score over two
            answers leading the table is a ranking by sample size.
          </dd>
        </div>
      </dl>

      <p className="mt-3">
        There are deliberately no targets and no pass/fail marks on this page: nobody has set one,
        and a threshold invented in the browser is an opinion dressed as a measurement.
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------

function Panel({
  id,
  title,
  subtitle,
  action,
  help,
  children,
}: {
  id?: string;
  /** Key into the help registry (`lib/help.ts`). Draws the ⓘ beside the title. */
  help?: string;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="group/card scroll-mt-4 rounded-xl border border-border bg-card p-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1">
            <h2 className="text-sm font-semibold">{title}</h2>
            {help && <InfoTip id={help} />}
          </div>
          {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

/**
 * One headline figure.
 *
 * ⚠️ A change in a rate is shown in PERCENTAGE POINTS, never in percent (D-53). "Up 4%" and "up
 * 4 points" are different claims and the second is the one the arithmetic supports.
 *
 * ⚠️ A movement is not coloured. Colouring a fall red on a page about named colleagues turns a
 * measurement into a verdict, which is what D-55 keeps off this screen.
 */
function Tile({
  label,
  value,
  text,
  delta,
  note,
}: {
  label: string;
  value?: number;
  text?: string;
  delta?: string | null;
  note?: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">
        {text ?? value?.toLocaleString() ?? "—"}
      </p>
      {delta && <p className="mt-0.5 text-xs text-muted-foreground">{delta}</p>}
      {note && <p className="mt-0.5 text-xs text-muted-foreground">{note}</p>}
    </div>
  );
}

/** A rate, or an em dash — never "0%" for an empty denominator (D-53). */
function percent(value: number | null): string {
  return value === null ? "—" : `${value.toFixed(1)}%`;
}

/**
 * The change in a rate, in percentage POINTS.
 *
 * ⚠️ Null when either side is missing, rather than treated as zero: "up 92 points from nothing"
 * is what a period with no answers produces, and it is not a fact about anybody's service.
 */
function points(now: number | null, before: number | null): string | null {
  if (now === null || before === null) return null;

  const delta = Math.round((now - before) * 10) / 10;

  if (delta === 0) return "No change on the previous period";

  return `${delta > 0 ? "+" : ""}${delta.toFixed(1)} points on the previous period`;
}

interface Options {
  teams: ReportFilterOption[];
  sites: ReportFilterOption[];
  categories: ReportFilterOption[];
}

/**
 * The filter options, from the reader's own unfiltered report (D-58).
 *
 * ⚠️ A row with no key is skipped — "Other" stands for several groups and has nothing to send.
 * The no-site row is the exception the server encodes as the literal `none`, because D-50 places
 * unmatched people nowhere and that set has to be selectable.
 */
function buildOptions(baseline: ReportSatisfaction | null): Options {
  const from = (breakdown: SatisfactionBreakdown | undefined): ReportFilterOption[] =>
    (breakdown?.rows ?? [])
      .filter((row) => row.key !== null)
      .map((row) => ({ id: row.key!, label: row.label, count: row.responses }));

  const sites = (baseline?.bySite.rows ?? [])
    .filter((row) => row.key !== null || row.label === "No site recorded")
    .map((row) => ({ id: row.key ?? "none", label: row.label, count: row.responses }));

  return {
    teams: from(baseline?.byTeam),
    sites,
    categories: from(baseline?.byCategory),
  };
}

/**
 * The filters already in the address bar, so a shared link opens the same question.
 *
 * ⚠️ Takes the reader's zone because the DEFAULT range is a calendar-day window and a calendar
 * day is only a pair of instants once a zone is known. An explicit range in the URL is already
 * a pair of instants and is used as sent.
 */
function readQueryFromUrl(zone: string): ReportQuery {
  const params = new URLSearchParams(window.location.search);

  const many = (key: string): string[] | undefined => {
    const values = params.getAll(key);
    return values.length > 0 ? values : undefined;
  };

  const range = lastDays(DEFAULT_DAYS, zone);

  return {
    fromUtc: params.get("raisedFromUtc") ?? range.fromUtc,
    toUtc: params.get("raisedToUtc") ?? range.toUtc,
    teamIds: many("teamIds"),
    siteIds: many("siteIds"),
    categoryIds: many("categoryIds"),
  };
}
